import { User } from '@hanwen/shared-types';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * 从localStorage获取初始认证状态
 */
export const getInitialAuthState = (): AuthState => {
  const token = localStorage.getItem('token');
  const userData = localStorage.getItem('user');
  
  if (!token || !userData) {
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
    };
  }
  
  try {
    const user = JSON.parse(userData);
    return {
      user,
      isAuthenticated: true,
      isLoading: false,
    };
  } catch (error) {
    console.warn('解析localStorage用户数据失败:', error);
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
    };
  }
};

/**
 * 通用的API请求函数
 */
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  apiBase: string = import.meta.env.VITE_API_URL || '',
  instanceId: string = 'unknown'
): Promise<T> => {
  const url = `${apiBase}${endpoint}`;
  
  try {
    // 从localStorage获取token
    const token = localStorage.getItem('token');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    // 如果存在token，添加到请求头
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // 调试日志
    console.log(`🔍 [${instanceId}] API请求:`, {
      url,
      method: options.method || 'GET',
      headers,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 10)}...` : '无',
    });
    
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    const responseText = await response.text();
    
    if (!responseText) {
      throw new Error('响应体为空');
    }
    
    const data: ApiResponse<T> = JSON.parse(responseText);

    // 调试日志
    console.log(`🔍 [${instanceId}] API响应:`, {
      url,
      status: response.status,
      success: data.success,
      error: data.error,
      hasData: !!data.data,
    });

    if (!data.success || !response.ok) {
      throw new Error(data.error || `API请求失败: ${response.status}`);
    }

    return data.data as T;
  } catch (error) {
    console.error(`API请求错误:`, error);
    throw error;
  }
};

/**
 * 处理登录响应，存储认证数据
 */
export const handleLoginResponse = (
  response: { user: User; token: string },
  setAuthState: (state: AuthState) => void
): User => {
  const { user, token } = response;
  
  // Store auth data
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  
  setAuthState({
    user,
    isAuthenticated: true,
    isLoading: false,
  });
  
  // 触发自定义事件，通知其他组件
  window.dispatchEvent(new Event('auth-changed'));
  
  return user;
};

/**
 * 处理登录错误，提供友好的错误消息
 */
export const handleLoginError = (error: unknown): Error => {
  if (error instanceof Error) {
    if (error.message.includes('401')) {
      return new Error('邮箱或密码错误');
    } else if (error.message.includes('网络') || error.message.includes('Network')) {
      return new Error('网络连接失败，请检查服务器状态');
    }
    return error;
  }
  return new Error('登录失败，请重试');
};

/**
 * 处理登出操作
 */
export const handleLogout = (setAuthState: (state: AuthState) => void): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  setAuthState({
    user: null,
    isAuthenticated: false,
    isLoading: false,
  });
  
  // 触发自定义事件，通知其他组件
  window.dispatchEvent(new Event('auth-changed'));
};

/**
 * 更新用户信息
 */
export const handleUpdateUser = (
  userData: Partial<User>,
  currentUser: User | null,
  setAuthState: (state: AuthState) => void
): void => {
  if (!currentUser) return;

  const updatedUser = { ...currentUser, ...userData };
  localStorage.setItem('user', JSON.stringify(updatedUser));
  
  setAuthState({
    user: updatedUser,
    isAuthenticated: true,
    isLoading: false,
  });
  
  // 触发自定义事件，通知其他组件
  window.dispatchEvent(new Event('auth-changed'));
};

/**
 * 验证token的有效性
 */
export const verifyToken = async (
  token: string,
  apiBase: string,
  instanceId: string
): Promise<User> => {
  return apiRequest<User>(
    '/api/auth/me',
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
    apiBase,
    instanceId
  );
};